/**
 * Pending Value Tracker
 *
 * Unified system for tracking pending dependency values and their processing state.
 * Replaces the previous dual system of pendingDependencyValues + processedHandlerValues.
 */

import { FieldPath } from './types';

interface PendingValue {
  /** The value waiting to be processed */
  value: any;
  /** Whether this value has been processed by a handler */
  processed: boolean;
  /** Timestamp when this value was set (for debugging) */
  timestamp: number;
}

/**
 * Tracks pending dependency values and their processing state
 */
export class PendingValueTracker {
  private pending = new Map<FieldPath, PendingValue>();

  /**
   * Sets a pending value for a field path
   *
   * @param path - The field path
   * @param value - The dependency value to store
   */
  set(path: FieldPath, value: any): void {
    this.pending.set(path, {
      value,
      processed: false,
      timestamp: Date.now(),
    });
  }

  /**
   * Gets the pending value for a field path
   * Returns undefined if no pending value or if already processed
   *
   * @param path - The field path
   * @returns The pending value or undefined
   */
  get(path: FieldPath): any | undefined {
    const entry = this.pending.get(path);
    if (!entry || entry.processed) {
      return undefined;
    }
    return entry.value;
  }

  /**
   * Gets the raw pending value without checking processed state
   * Useful for debugging
   *
   * @param path - The field path
   * @returns The pending value or undefined
   */
  getRaw(path: FieldPath): any | undefined {
    return this.pending.get(path)?.value;
  }

  /**
   * Marks a pending value as processed
   *
   * @param path - The field path
   */
  markProcessed(path: FieldPath): void {
    const entry = this.pending.get(path);
    if (entry) {
      entry.processed = true;
    }
  }

  /**
   * Checks if a field has a pending value (regardless of processed state)
   *
   * @param path - The field path
   * @returns True if a pending value exists
   */
  has(path: FieldPath): boolean {
    return this.pending.has(path);
  }

  /**
   * Checks if a pending value has been processed
   *
   * @param path - The field path
   * @returns True if processed, false if not processed or doesn't exist
   */
  isProcessed(path: FieldPath): boolean {
    return this.pending.get(path)?.processed || false;
  }

  /**
   * Deletes a pending value
   *
   * @param path - The field path
   */
  delete(path: FieldPath): void {
    this.pending.delete(path);
  }

  /**
   * Clears all pending values
   */
  clear(): void {
    this.pending.clear();
  }

  /**
   * Resets all processed flags (marks all as unprocessed)
   * Useful when a new dependency update arrives
   */
  resetProcessedFlags(): void {
    for (const entry of this.pending.values()) {
      entry.processed = false;
    }
  }

  /**
   * Gets all pending values with their paths
   * Useful for debugging
   *
   * @returns Array of [path, value] tuples
   */
  getAll(): Array<[FieldPath, any]> {
    return Array.from(this.pending.entries()).map(([path, entry]) => [
      path,
      entry.value,
    ]);
  }

  /**
   * Gets statistics about pending values (for debugging)
   */
  getStats(): {
    total: number;
    processed: number;
    unprocessed: number;
  } {
    let processed = 0;
    let unprocessed = 0;

    for (const entry of this.pending.values()) {
      if (entry.processed) {
        processed++;
      } else {
        unprocessed++;
      }
    }

    return {
      total: this.pending.size,
      processed,
      unprocessed,
    };
  }
}
